/**
 * Password hashing utility that implements the same algorithm as the C++ UserMgr
 *
 * The algorithm:
 * 1. Generate a random salt (64 bytes as hex string)
 * 2. Create hash input as: username:password:salt
 * 3. Hash using SHA512
 * 4. Return both hex-encoded hash and salt
 */

const SALT_LENGTH = 64; // 64 bytes = 128 hex characters

/**
 * Generate a random salt as hex string
 */
export function generateSalt(): string {
  const buffer = new Uint8Array(SALT_LENGTH);
  crypto.getRandomValues(buffer);

  return Array.from(buffer)
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Hash password using SHA512 with the same algorithm as C++ UserMgr
 */
export async function hashPassword(
  username: string,
  password: string,
  salt: string,
): Promise<string> {
  const toHash = `${username}:${password}:${salt}`;
  const encoder = new TextEncoder();
  const data = encoder.encode(toHash);

  const hashBuffer = await crypto.subtle.digest('SHA-512', data);
  const hashArray = new Uint8Array(hashBuffer);

  return Array.from(hashArray)
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Create a new password hash with generated salt
 */
export async function createPasswordHash(
  username: string,
  password: string,
): Promise<{
  hash: string;
  salt: string;
}> {
  const salt = generateSalt();
  const hash = await hashPassword(username, password, salt);

  return { hash, salt };
}

/**
 * Verify a password against stored hash and salt
 */
export async function verifyPassword(
  username: string,
  password: string,
  storedHash: string,
  storedSalt: string,
): Promise<boolean> {
  const computedHash = await hashPassword(username, password, storedSalt);
  return computedHash === storedHash;
}
